import { captureException, startSpan } from "@sentry/react";

import {
    IDeleteImageRequest,
    IGenerateImageResponse,
    IImageResponse,
    IImagesSearchResponse,
    ImageMetadata,
    ImageSearchQueryDTO,
    IPollGenerateImage,
    IRecentImageResponse,
    IUpdateImageResponse,
    IVisibilityRequest,
    IVoteRequest,
} from "../../types";
import {
    doDelete,
    doDeleteWithJson,
    doGet,
    doGetOptAuth,
    doPost,
    doPostJson,
    doPostJsonOptAuth,
    doPutJson,
} from "../core/http";

const wait = (ms: number = 1000) => {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
};

export const poll = async (
    fn: any,
    fnCondition: (e: any) => boolean,
    ms: number
) => {
    let result = await fn();

    while (fnCondition(result)) {
        await wait(ms);
        result = await fn();
    }

    return result;
};

export const pollGenerateImage = async (
    requestId: string,
    isPremium: boolean = false
): Promise<IPollGenerateImage> => {
    try {
        const image = isPremium
            ? `/v2/image/premium/generate/${requestId}/poll`
            : `/v2/image/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollGenerateImage" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            captureException(error);
            return { error: error };
        }

        return {
            image: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get image." };
    }
};

export const generateImage = async (
    formData: FormData,
    isPremium: boolean = false
): Promise<IGenerateImageResponse> => {
    const url = isPremium ? "/v2/image/premium/generate" : "/v2/image/generate";

    try {
        const { data, error } = await startSpan(
            { name: "generateImage" },
            () => {
                return doPost(url, true, formData);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." };
    }
};

export const uploadImage = async (
    formData: FormData
): Promise<IGenerateImageResponse> => {
    const url = "/v2/image/upload";

    try {
        const { data, error } = await startSpan({ name: "uploadImage" }, () => {
            return doPost(url, true, formData);
        });

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." };
    }
};

export const editImage = async (
    formData: FormData
): Promise<IGenerateImageResponse> => {
    const url = "/v2/image/edit";

    try {
        const { data, error } = await startSpan({ name: "editImage" }, () => {
            return doPost(url, true, formData);
        });

        if (error) {
            console.error(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        console.error(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." };
    }
};

export const getMostRecentPublicImages = async (
    cursor: Date | string = new Date(), // Allow string for ISO format
    size: number
): Promise<IRecentImageResponse> => {
    try {
        const cursorIsoString =
            cursor instanceof Date ? cursor.toISOString() : cursor;

        const url = `/v1/images/public?cursor=${cursorIsoString}&size=${size.toString()}`;
        const { data, error } = await startSpan(
            { name: "getMostRecentPublicImages" },
            () => {
                return doGet(url, false);
            }
        );

        if (error) {
            captureException(error);
            return { error };
        }

        return {
            image: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get fields" }; // Original code had this error message, might need update?
    }
};

export const onToggleVisibility = async (
    imageId: string,
    isVisible: boolean = false
): Promise<{ error?: string }> => {
    const url = `/v1/images/${imageId}/visibility`;
    const payload: IVisibilityRequest = {
        isVisible: isVisible,
    };

    try {
        const { error } = await startSpan(
            { name: "onToggleVisibility" },
            () => {
                return doPostJson(url, true, payload);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to set image visibility." };
    }
};

export const onToggleNSFW = async (
    imageId: string
): Promise<{ error?: string }> => {
    const url = `/v1/images/${imageId}/nsfw`;

    try {
        const { error } = await startSpan({ name: "onToggleNSFW" }, () => {
            return doGet(url, true);
        });

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to set image NSFW status." };
    }
};

export const voteOnImage = async (
    imageId: string,
    isUpvote: boolean = false
): Promise<{ error?: string }> => {
    const url = `/v1/images/${imageId}/vote`;
    const payload: IVoteRequest = {
        isUpvote: isUpvote,
    };
    try {
        const { error } = await startSpan({ name: "voteOnImage" }, () => {
            return doPostJson(url, true, payload);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to vote on image." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to vote on image." };
    }
};

export const deleteImage = async (
    imageId: string
): Promise<{ error?: string }> => {
    const url = `/v1/images/${imageId}`;
    try {
        const { error } = await startSpan({ name: "deleteImage" }, () => {
            return doDelete(url, true);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to delete image." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to delete image." };
    }
};

export const deleteAllImages = async (
    dataPayload: IDeleteImageRequest
): Promise<{ error?: string }> => {
    const url = "/v1/images/delete/all";
    try {
        const { error } = await startSpan({ name: "deleteAllImages" }, () => {
            // Updated span name
            return doDeleteWithJson(url, true, dataPayload);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to delete images." }; // Updated error message
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to delete images." }; // Updated error message
    }
};

export const searchImages = async (
    params: ImageSearchQueryDTO
): Promise<IImagesSearchResponse> => {
    try {
        const response = await startSpan({ name: "searchImages" }, async () => {
            return doPostJsonOptAuth("/api/v1/search/images", params);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        // Expect backend to return { images, totalCount }
        return {
            images: response.data.images,
            totalCount: response.data.totalCount,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get images" };
    }
};

export const getImage = async (imageId: string): Promise<IImageResponse> => {
    try {
        const response = await startSpan({ name: "getImage" }, async () => {
            return doGetOptAuth(`/v1/images/${imageId}`);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return {
            image: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get images" }; // Changed error message for consistency
    }
};

export const updateImage = async (
    imageId: string,
    imageMetadata: ImageMetadata
): Promise<IUpdateImageResponse> => {
    const url = `/v1/images/${imageId}`;
    try {
        const response = await startSpan({ name: "updateImage" }, async () => {
            // Payload is the new image metadata
            return doPutJson(url, true, { ...imageMetadata });
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        // Assuming the server responds with the updated image
        return { image: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to update image." };
    }
};

export const pollEditImage = async (
    requestId: string
): Promise<IPollGenerateImage> => {
    try {
        // NOTE: Original code pointed to NPC poll endpoint. This might be an error in original code.
        // Assuming it should poll a generic edit endpoint if one exists, or this specific one is intended.
        const image = `/v1/npc/image/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollEditImage" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {
            image: data,
        };
    } catch (e) {
        console.error(e);
        return { error: "Failed to get image." };
    }
};

export const pollGenerateNpcImage = async (
    requestId: string,
    isPremium: boolean = false
): Promise<IPollGenerateImage> => {
    try {
        const image = isPremium
            ? `/v1/npc/image/premium/generate/${requestId}/poll`
            : `/v1/npc/image/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollGenerateNpcImage" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {
            image: data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get image." };
    }
};

export const pollGenerateMonsterImage = async (
    requestId: string,
    isPremium: boolean = false
): Promise<IPollGenerateImage> => {
    try {
        const image = isPremium
            ? `/v1/monster/image/premium/generate/${requestId}/poll`
            : `/v1/monster/image/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollGenerateMonsterImage" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            console.error(error); // Original used console.error
            return { error: error.message };
        }

        return {
            image: data,
        };
    } catch (e) {
        console.error(e); // Original used console.error
        return { error: "Failed to get image." };
    }
};
